import React from 'react';

import Thumb from '../img/blogs/10-things-that-every-web-developer-should-know.jpg';
import DOMTree from '../img/blog-details/10-things-that-every-web-developer-should-know/dom-tree.png';

const TenThingsEveryWebDeveloperShouldKnow = {
    id: 5, permalink: "10-things-that-every-web-developer-should-know", thumb: Thumb, postBy: "Wasek Bellah", postDate: "Apr 29, 2020", categories: "JavaScript", title: "10 things that every Web Developer should know", shortDesc: "Web Developer should know about Coding Style, Comments, Error handling, Custom errors, DOM tree, Walking the DOM, Node properties, Attributes and properties, Modifying the document, Styles and classes", fullDesc: <>
        <h2>Coding Style:</h2>
        <p>When we think of writing "<strong>better</strong>" code, the questions we should ask ourselves are: "<strong>Does the code make it more readable and easier to understand?</strong>" And "<strong>What can help us avoid errors?</strong>" These are the main things to keep in mind when selecting and debating code styles.</p>

        <h4>Here are some suggestions for <strong>Codding Style</strong>:</h4>
        <ul>
            <li>No space between the function name and parentheses, between the parentheses and the parameter. <code>function add(x, y)</code></li>
            <li>A space between parameters. <code>function add(x, y)</code></li>
            <li>Curly brace &#123; on the same line, after a space. <code>function add(x, y) &#123;</code></li>
            <li>Horizontal indents: 2 or 4 spaces in the nested code. <code>    &nbsp; return x + y;</code></li>
            <li>Spaces around operators. <code>for (let i = 0; 0 &lsaquo; n; i++)</code></li>
            <li>A semicolon( ; ) is mandatory when terminated a statement. <code>let name = "Wasek Bellah";</code></li>
            <li>An empty line between logical blocks.</li>
            <li>Lines are not very long.</li>
            <li>&#123; else &#123; without a line break. <code>&#123; else &#123;</code></li>
            <li>Spaces around a nested call. <code>console.log( add(4, 3) )</code></li>
        </ul>

        <p><em>Some <strong>bad practices</strong> and <strong>good practices</strong> of <strong>JavaScript</strong> Code</em>:</p>
        <h4>Bad practice:</h4>
        <pre className="code">
            if(condition) <br />
            &#123; <br />
            If block......... <br />
            &#125; <br />
            else <br />
            &#123; <br />
            Else block......... <br />
            &#125;
        </pre>

        <h4>Good practice:</h4>
        <pre className="code">
            if(condition) &#123; <br />
                &nbsp; If block......... <br />
            &#125; else &#123; <br />
                &nbsp; Else block......... <br />
            &#125;
        </pre>

        <h4>Bad practice:</h4>
        <pre className="code">
            if (condition) &#123;console.log("Condition is true");&#125;
        </pre>

        <h4>Acceptable, if it’s short:</h4>
        <pre className="code">
            if (condition) console.log("Condition is true");
        </pre>

        <h4>Good practice:</h4>
        <pre className="code">
            if(condition) &#123; <br />
                &nbsp; console.log("Condition is true"); <br />
            &#125;
        </pre>


        <h2>Comments:</h2>
        <p>Programs become more complex as time goes on. It is necessary to add comments that describe what the code does and why.</p>

        <p>Comments can be placed anywhere in a script. They do not affect its application because the engine simply ignores them.</p>

        <p><em><strong>One-line comments</strong> begin with two <strong>forward slash</strong> characters <strong>&#47;&#47;</strong></em></p>
        <pre className="code">
            &#47;&#47; This is single line comment, and comment dose not effect on code
        </pre>

        <p><em><strong>Multiline comments</strong> begin with a <strong>forward slash and an asterisk &#47;*</strong> and end with an <strong>asterisk and a forward slash *&#47;</strong></em></p>
        <pre className="code">
            &#47;* <br />
                &nbsp; This <br />
                &nbsp; is <br />
                &nbsp; multiline <br />
                &nbsp; comment <br />
            *&#47;
        </pre>

        <p>An important sign of a good developer comment: their presence or even their absence.</p>

        <p>Good comments allow us to better maintain the code, return to it after a delay, and use it more effectively.</p>

        <h4>Comment on:</h4>
        <ul>
            <li>The overall architecture, high-level view.</li>
            <li>Function usage.</li>
            <li>Important solutions, especially when not immediately obvious.</li>
        </ul>

        <h4>Avoid comments:</h4>
        <ul>
            <li>It says "how the code works" and "what it does".</li>
            <li>It's impossible to make codes so simple and self-explanatory that they just don't need them.</li>
        </ul>


        <h2>Error handling: (try..catch):</h2>
        <p>The <code>try..catch</code> construct allows us to manage runtime errors. It literally allows us to "<strong>try</strong>" running the code and "<strong>catch</strong>" errors that may occur in it.</p>

        <p><em>The <code>try..catch</code> construct has two main blocks: <code>try</code>, and then <code>catch</code>:</em></p>
        <pre className="code">
            try &#123; <br />
                &nbsp; &#47;&#47; code... <br />
            &#125; catch (err) &#123; <br />
                &nbsp; &#47;&#47; error handling <br />
            &#125;
        </pre>

        <ul>
            <li>First, the code in try &#123;...&#125; is executed.</li>
            <li>If there were no errors, then catch(err) is ignored.</li>
            <li>If an error occurs, then the try execution is stopped, and control flows to the beginning of catch(err). We can use any name for parameter, it will contain an error object with details about what happened.</li>
        </ul>

        <pre className="code">
            try &#123; <br />
                &nbsp; console.log('try runs starts'); <br />
                &nbsp; name; &#47;&#47; variable is not defined! <br />
                &nbsp; console.log('try runs end (never reached)'); <br />
            &#125; catch(err) &#123; <br />
                &nbsp; console.log(err); <br />
            &#125;
        </pre>
        <p><strong>Expected output:</strong> <code className="output">try runs starts</code> and <code className="output">ReferenceError: name is not defined</code></p>


        <h2>Custom errors:</h2>
        <p>In <strong>JavaScript</strong>, we have an <strong>Error class</strong> to throw exceptions. We can create custom <strong>Error classes</strong> by expanding the <strong>Error class</strong> with our own class and custom reasoning to throw more specific errors.</p>

        <p>The <strong>Error class</strong> contains the message, name and stack properties that we got from it.</p>

        <pre className="code">
            class Error &#123; <br />
                &nbsp; constructor(message) &#123; <br />
                    &nbsp;   this.message = message; <br />
                    &nbsp;   this.name = "Error"; <br />
                    &nbsp;   this.stack = &lsaquo;call stack&rsaquo;; &#47;&#47; non-standard, but most environments support it <br />
                &nbsp; &#125; <br />
            &#125; <br /><br />

            function test() &#123; <br />
                &nbsp; throw new Error("Whoops! there an error."); <br />
            &#125; <br /><br />

            try &#123; <br />
                &nbsp; test(); <br />
            &#125; catch(err) &#123; <br />
                &nbsp; console.log(err.message); <br />
                &nbsp; console.log(err.name); <br />
                &nbsp; console.log(err.stack); <br />
            &#125;
        </pre>
        <p><strong>Expected output:</strong> <code className="output">Whoops! there an error.</code>, <code className="output">Error</code> and A list of nested calls with line numbers for each.</p>


        <h2>DOM tree:</h2>
        <p>The tags are the backbone of the <strong>HTML Document</strong>. According to the <strong>Document Object Model (DOM)</strong>, each HTML tag is an <strong>object</strong>. Nested tags are "<strong>children</strong>" of enclosing one. The text inside a tag is also an <strong>object</strong>.</p>

        <p>HTML <strong>DOM</strong> allows <strong>JavaScript</strong> to access and modify all elements of an <strong>HTML Document</strong>.</p>

        <p>When a web page loads, the browser creates a <strong>Document Object Model</strong> of the page.</p>

        <pre className="code">
            &lsaquo;!DOCTYPE HTML&rsaquo; <br />
            &lsaquo;html&rsaquo; <br />
                &nbsp; &lsaquo;head&rsaquo; <br />
                    &nbsp;   &lsaquo;title&rsaquo;About elk&lsaquo;/title&rsaquo; <br />
                &nbsp; &lsaquo;&#47;head&rsaquo; <br />
                &nbsp; &lsaquo;body&rsaquo; <br />
                    &nbsp;   The truth about elk. <br />
                &nbsp; &lsaquo;&#47;body&rsaquo; <br />
            &lsaquo;&#47;html&rsaquo;
        </pre>

        <p><em><strong>DOM</strong> represents <strong>HTML</strong> as a tree structure of tags. Here's what it looks like</em>:</p>
        <img src={DOMTree} alt="DOM Tree" />

        <p><em>Please note the special characters in text nodes</em>:</p>
        <ul>
            <li>A newline: <code>↵</code> (in <strong>JavaScript</strong> known as <code>&#92;n</code>)</li>
            <li>A space: <code>␣</code></li>
        </ul>

        <p><em>There are two top-level exclusions</em>:</p>
        <ul>
            <li>Spaces and newlines before <code>&lsaquo;head&rsaquo;</code> are ignored for historical reasons.</li>
            <li>If we put something after <code>&lsaquo;body&rsaquo;</code>, then it automatically moves inside the <strong>body</strong>, because all content must be inside <code>&lsaquo;body&rsaquo;</code> for <strong>HTML</strong> estimation. So there can be no place after <code>&lsaquo;body&rsaquo;</code>.</li>
        </ul>

        <p>The top tag is always <code>&lsaquo;html&rsaquo;</code>. Even if it does not exist in the document, it will be present in the DOM because the browser will create it. The same is true of <code>&lsaquo;body&rsaquo;</code>.</p>


        <h2>Walking the DOM:</h2>
        <p>The <strong>DOM</strong> allows us to do anything with elements and their contents.</p>

        <p>All activities in the <strong>DOM</strong> begin with the document object. This is the main "entrance" to the <strong>DOM</strong>. From this, we can access any node.</p>

        <p><em>The top <strong>tree nodes</strong> are directly available as document properties</em>:</p>
        <pre className="code">&lsaquo;html&rsaquo; = document.documentElement</pre>
        <p>The top document node is <code>document.documentElement</code>. It’s the <strong>DOM node</strong> of the <code>&lsaquo;html&rsaquo;</code> tag.</p>

        <pre className="code">&lsaquo;body&rsaquo; = document.body</pre>
        <p><code>document.body</code> is <strong>DOM node</strong> of the <code>&lsaquo;body&rsaquo;</code> tag.</p>

        <pre className="code">&lsaquo;head&rsaquo; = document.head</pre>
        <p><code>document.head</code> is <strong>DOM node</strong> of the <code>&lsaquo;head&rsaquo;</code> tag.</p>


        <h2>Node properties:</h2>
        <p>Different <strong>DOM nodes</strong> may have different properties. <strong>Text nodes</strong> are not like <strong>element nodes</strong>. However, they also have common features and methods because the <strong>DOM nodes</strong> of all classes form a single classification.</p>

        <p>Each <strong>DOM node</strong> belongs to the corresponding built-in <strong>class</strong>.</p>

        <p><em>The classes are</em>:</p>
        <ul>
            <li><strong>EventTarget</strong> – is the root "<strong>abstract</strong>" class. <strong>Objects</strong> of that class are never created. It acts as a base.</li>
            <li><strong>Node</strong> – is also an "<strong>abstract</strong>" class, which serves as the base of the <strong>DOM node</strong>. It provides key tree functionality: <code>parentNode</code>, <code>nextSibling</code>, <code>childNodes</code> and more.</li>
            <li><strong>Element</strong> – is a base class for <strong>DOM elements</strong>. It provides <strong>element-level</strong> navigation such as <code>nextElementSibling</code>, <strong>children</strong> and searching methods like <code>getElementsByTagName</code>, <code>querySelector</code>.</li>
            <li><strong>HTMLElement</strong> – is finally the basic class for all <strong>HTML elements</strong>.</li>
            <li></li>
        </ul>


        <h2>Attributes and properties:</h2>
        <p>When the browser loads the page, it reads the <strong>HTML</strong> and creates a <strong>DOM object</strong> from it. For element nodes, most standard <strong>HTML attributes</strong> automatically become properties of the <strong>DOM object</strong>.</p>

        <p>If the tag is <code>&lsaquo;h2 id="secondHeading"&rsaquo;&lsaquo;h2&rsaquo;</code>, then the DOM object has <code>h2.id="secondHeading"</code>.</p>


        <h2>Modifying the document:</h2>
        <p><strong>DOM modification</strong> is the key to creating "<strong>live</strong>" pages.</p>

        <p><em>Methods to create new nodes</em>:</p>
        <ul>
            <li><code>document.createElement(tag)</code> <em>creates an element with the given tag</em>.</li>
            <li><code>document.createTextNode(value)</code> <em>creates a text node (rarely used)</em>.</li>
            <li><code>element.cloneNode(deep)</code> <em>clones the element, if <code>deep==true</code> then with all descendants</em>.</li>
        </ul>

        <p><em>Insertion and removal</em>:</p>
        <ul>
            <li><code>node.append(...nodes or strings)</code> <em>insert into <code>node</code>, at the end</em>.</li>
            <li><code>node.prepend(...nodes or strings)</code> <em>insert into <code>node</code>, at the beginning</em>.</li>
            <li><code>node.before(...nodes or strings)</code> <em>insert right before <code>node</code></em>.</li>
            <li><code>node.after(...nodes or strings)</code> <em>insert right after <code>node</code></em>.</li>
            <li><code>node.replaceWith(...nodes or strings)</code> <em>replace <code>node</code></em>.</li>
            <li><code>node.remove()</code> <em>remove the <code>node</code></em>.</li>
        </ul>


        <h2>Styles and classes:</h2>
        <p><strong>JavaScript</strong> can modify both <strong>classes</strong> and <code>style</code> properties.</p>

        <h3>className and classList</h3>
        <p>By using <code>className</code> we can catch that <strong>element</strong>.</p>

        <pre className="code">
            &lsaquo;body class="body bodyClass"&rsaquo; <br />
                &nbsp; &lsaquo;script&rsaquo; <br />
                    &nbsp;   console.log(document.body.className); <br />
                &nbsp; &lsaquo;/script&rsaquo; <br />
            &lsaquo;/body&rsaquo;
        </pre>
        <p><strong>Expected output:</strong> <code class="output">body bodyClass</code></p>

        <p>By using <code>classList</code> we can add or remove "class" from that <strong>element</strong>.</p>

        <pre className="code">
            &lsaquo;body class="body bodyClass"&rsaquo; <br />
                &nbsp; &lsaquo;script&rsaquo; <br />
                    &nbsp;   document.body.classList.add('newClass'); <br />
                    &nbsp;   console.log(document.body.className); <br />
                &nbsp; &lsaquo;/script&rsaquo; <br />
            &lsaquo;/body&rsaquo;
        </pre>
        <p><strong>Expected output:</strong> <code class="output">body bodyClass newClass</code></p>
    </>
}
export default TenThingsEveryWebDeveloperShouldKnow;